import React, { useEffect, useMemo, useState } from 'react';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
  getSortedRowModel,
  SortingState,
  Updater,
} from '@tanstack/react-table';

import { DefaultTable, LinkCell } from '@hud/web-components/Table/Table';
import { formatDatetime, renderDatetime } from '@hud/web-components/Table/CellRenderers';
import TableContainer from '@hud/web-components/Container/TableContainer';
import { Search } from '@hud/web-components/Search/Search';
import { Endpoint } from '@hud/portal-dtos/webapp-endpoint.dto';
import Pagination from '@hud/web-components/Pagination/Pagination';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import { useGlobalQueryString } from '../../hooks/useGlobalFilter';
import LongContentPopover from '@hud/web-components/LongContentPopover/LongContentPopover';
import renderTrendHeader from '../trendHeaderRenderer/trendHeaderRenderer';
import renderMetricTrend, {
  formatMetricTrend,
} from '../metricTrendRenderer/metricTrendRenderer';
import renderFunctionMetric, {
  formatFunctionMetric,
} from '../functionMetricCellRenderer/functionMetricCellRenderer';
import renderMethods from '../endpointsCellRenderer/methodsCellRenderer';
import { HorizontalContainer } from '@hud/web-components/Container/HorizontalContainer';
import {
  ClearAllButton,
  FiltersIcon,
  FiltersMenuButton,
} from '../FunctionsTable/FunctionsTable.styles';
import Menu from '@hud/web-components/Menu/Menu';
import tooltips from '../../assets/tooltips';
import ServicesFilter from '../ServicesFilter/ServicesFilter';
import { useUserPreferences } from '../../hooks/useUserPreferences';
import { ThresholdFilter as ThresholdFilterComponent } from '@hud/web-components/Filters/ThresholdFilter';
import { ThresholdFilter, TimeFrame } from '@hud/portal-dtos/webapp-common.dto';
import {
  DEFAULT_PAGE_SIZE,
  DURATION_AVG_DEFAULT,
  NORMALIZED_RUNS_DEFAULT,
  ERROR_RATE_DEFAULT,
  DURATION_AVG_PRESETS,
  NORMALIZED_RUNS_PRESETS,
  ERROR_RATE_PRESETS,
} from '../../consts';
import p90RenderFunctionMetric from '../functionMetricCellRenderer/p90MetricCellRenderer';
import EndpointsTableEmptyState from './EndpointsTableEmptyState';
import renderRunsMetric from '../metricCellRenderer/runsCellRenderer';

const columnHelper = createColumnHelper<Endpoint>();

export interface EndpointsTableProps {
  endpoints: Endpoint[];
  total?: number;
  filtered?: number;
  loading: boolean;
  timeFrame: TimeFrame;

  searchValue: string;
  onSearchChange: (searchValue: string) => void;

  sortingState: SortingState;
  onSortingChange: (updater: Updater<SortingState>) => void;

  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;

  filteredServices: number[];
  onFilteredServicesChange: (value: number[]) => void;

  filteredDurationAvg?: ThresholdFilter;
  onFilteredDurationAvgChange?: (value: ThresholdFilter | undefined) => void;
  filteredNormalizedRuns?: ThresholdFilter;
  onFilteredNormalizedRunsChange?: (value: ThresholdFilter | undefined) => void;
  filteredErrorRate?: ThresholdFilter;
  onFilteredErrorRateChange?: (value: ThresholdFilter | undefined) => void;

  onClearAllFilters: () => void;
  fullPageTable?: boolean;
}

type FilterType = 'service' | 'durationAvg' | 'normalizedRuns' | 'errorRate';

interface Filter {
  type: FilterType;
  // Additional properties that each filter might need for its specific configuration
  props?: unknown; // Use more specific types based on actual requirements
}

export default function EndpointsTable({
  endpoints,
  loading,
  total = 0,
  filtered = 0,
  searchValue,
  onSearchChange,
  sortingState,
  onSortingChange,
  pages,
  currentPage,
  onPageChange,
  filteredServices,
  onFilteredServicesChange,
  filteredDurationAvg,
  onFilteredDurationAvgChange,
  filteredNormalizedRuns,
  onFilteredNormalizedRunsChange,
  filteredErrorRate,
  onFilteredErrorRateChange,
  fullPageTable,
  onClearAllFilters,
  timeFrame,
}: EndpointsTableProps) {
  const globalFilters = useGlobalQueryString();
  const { userPreferredServices } = useUserPreferences();

  const initializeFilters = (): Filter[] => {
    const initialFilters: Filter[] = [];

    if (filteredServices && filteredServices?.length > 0) {
      initialFilters.push({
        type: 'service',
        props: { ids: filteredServices },
      });
    }
    if (filteredDurationAvg) {
      initialFilters.push({
        type: 'durationAvg',
        props: { durationAvg: filteredDurationAvg },
      });
    }
    if (filteredNormalizedRuns) {
      initialFilters.push({
        type: 'normalizedRuns',
        props: { normalizedRuns: filteredNormalizedRuns },
      });
    }
    if (filteredErrorRate) {
      initialFilters.push({
        type: 'errorRate',
        props: { errorRate: filteredErrorRate },
      });
    }
    return initialFilters;
  };

  const [activeFilters, setActiveFilters] = useState<Filter[]>(initializeFilters);

  const handleFilterChange = (
    filterType: FilterType,
    isActive: boolean,
    filterProps = {},
  ) => {
    if (isActive) {
      if (activeFilters.some((f) => f.type === filterType)) return;
      setActiveFilters((prev) => [
        ...prev.filter((f) => f.type !== filterType),
        { type: filterType, ...filterProps },
      ]);
    } else {
      setActiveFilters((prev) => prev.filter((filter) => filter.type !== filterType));
    }
  };

  const toggleServiceFilter = (isActive: boolean) => {
    handleFilterChange('service', isActive, { ids: filteredServices });
  };

  const toggleDurationAvgFilter = (isActive: boolean) => {
    handleFilterChange('durationAvg', isActive, {
      durationAvg: DURATION_AVG_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'durationAvg')) {
      onFilteredDurationAvgChange?.(DURATION_AVG_DEFAULT);
    }
  };

  const toggleNormalizedRunsFilter = (isActive: boolean) => {
    handleFilterChange('normalizedRuns', isActive, {
      normalizedRuns: NORMALIZED_RUNS_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'normalizedRuns')) {
      onFilteredNormalizedRunsChange?.(NORMALIZED_RUNS_DEFAULT);
    }
  };

  const toggleErrorRateFilter = (isActive: boolean) => {
    handleFilterChange('errorRate', isActive, {
      errorRate: ERROR_RATE_DEFAULT,
    });
    if (isActive && !activeFilters.some((f) => f.type === 'errorRate')) {
      onFilteredErrorRateChange?.(ERROR_RATE_DEFAULT);
    }
  };

  useEffect(() => {
    setActiveFilters(initializeFilters());
  }, [filteredServices]);

  const columns = useMemo(() => {
    const defaultColumns = [
      columnHelper.accessor('service', {
        header: 'Service',
        cell: (cell) => {
          return (
            <LinkCell to={`/explore/services/${cell.getValue().id}?${globalFilters}`}>
              <LongContentPopover>{cell.getValue().name}</LongContentPopover>
            </LinkCell>
          );
        },
        size: 100,
        maxSize: 200,
        minSize: 110,
        meta: {
          fitFormatter: (cell) => cell.getValue().name,
          disableAutoFit: true,
        },
      }),
      columnHelper.accessor('path', {
        header: 'Path',
        cell: (cell) => {
          return (
            <LinkCell
              to={`/explore/endpoints/${cell.row.original.id}-${cell.row.original.service.id}?${globalFilters}`}
            >
              <LongContentPopover>{cell.getValue()}</LongContentPopover>
            </LinkCell>
          );
        },
        meta: {
          copyable: true,
          fitFormatter: (cell) => cell.getValue(),
          disableAutoFit: true,
        },
        minSize: 150,
        size: 250,
      }),
      columnHelper.accessor('methods', {
        header: 'Methods',
        cell: renderMethods,
        size: 140,
        maxSize: 140,
        minSize: 120,
        enableSorting: false,
        meta: {},
      }),
      columnHelper.accessor('metrics.runs.value', {
        id: 'runs.value',
        header: 'Runs (Runs/Day)',
        cell: renderRunsMetric(timeFrame),
        minSize: timeFrame === 'hour' ? 120 : 200,
        maxSize: 250,
        meta: {
          fitFormatter: formatFunctionMetric('number'),
          description: tooltips.ENDPOINTS_TABLE.RUNS,
          rightAlign: true,
        },
      }),
      columnHelper.accessor('metrics.runs', {
        id: 'runs.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.RUNS_TREND,
        },
      }),
      columnHelper.accessor('metrics.durationAvg.value', {
        id: 'durationAvg.value',
        header: 'Dur Avg',
        cell: renderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.DURATION,
        },
      }),
      columnHelper.accessor('metrics.durationAvg', {
        id: 'durationAvg.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.AVG_DURATION_TREND,
        },
      }),
      columnHelper.accessor('metrics.durationP90.value', {
        id: 'durationP90.value',
        header: 'Dur P90',
        cell: p90RenderFunctionMetric('duration'),
        size: 130,
        maxSize: 200,
        minSize: 100,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.DURATION_P90,
        },
      }),
      columnHelper.accessor('metrics.durationP90', {
        id: 'durationP90.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.DURATION_P90_TREND,
        },
      }),
      columnHelper.accessor('metrics.errorRate.value', {
        id: 'errorRate.value',
        header: 'Err rate',
        cell: renderFunctionMetric('percent'),
        size: 130,
        minSize: 100,
        maxSize: 200,
        meta: {
          fitFormatter: formatFunctionMetric('duration'),
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.ERRORS,
        },
      }),
      columnHelper.accessor('metrics.errorRate', {
        id: 'errorRate.trend',
        header: renderTrendHeader,
        cell: renderMetricTrend,
        enableSorting: true,
        enableResizing: false,
        size: 40,
        minSize: 40,
        meta: {
          narrow: true,
          fitFormatter: formatMetricTrend,
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.ERRORS_TREND,
        },
      }),
      columnHelper.accessor('lastSeenAt', {
        header: 'Last invoked',
        cell: renderDatetime(true),
        size: 160,
        maxSize: 200,
        meta: {
          fitFormatter: formatDatetime(true),
          rightAlign: true,
          description: tooltips.ENDPOINTS_TABLE.LAST_INVOKED,
        },
      }),
    ];
    return defaultColumns;
  }, [globalFilters]);

  const table = useReactTable({
    data: endpoints,
    columns: columns,
    state: {
      sorting: sortingState,
    },

    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),

    onSortingChange,

    columnResizeMode: 'onChange',
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    enableSortingRemoval: false,

    defaultColumn: {
      minSize: 130,
    },
  });

  const hasFilters = activeFilters.length > 0;

  return (
    <TableContainer.Container
      title={loading ? <Skeleton width={140} /> : `Showing ${filtered}/${total}`}
    >
      <TableContainer.Header>
        <TableContainer.Filters>
          <Search inline value={searchValue} onSearchChange={onSearchChange} />
          {activeFilters.map((filter) => {
            switch (filter.type) {
              case 'service':
                return (
                  <ServicesFilter
                    userPreferredServices={userPreferredServices ?? []}
                    filteredServices={filteredServices}
                    onFilteredServicesChange={onFilteredServicesChange}
                    handleFilterChange={handleFilterChange}
                  />
                );
              case 'durationAvg':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Duration Avg"
                    value={filteredDurationAvg?.value ?? 0}
                    operator={filteredDurationAvg?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredDurationAvgChange?.({
                        ...(filteredDurationAvg as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredDurationAvgChange?.({
                        ...(filteredDurationAvg as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={DURATION_AVG_PRESETS}
                    unit="ms"
                    onClear={() => {
                      onFilteredDurationAvgChange?.(undefined);
                      toggleDurationAvgFilter(false);
                    }}
                  />
                );
              case 'normalizedRuns':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Runs/d"
                    value={filteredNormalizedRuns?.value ?? 0}
                    operator={filteredNormalizedRuns?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredNormalizedRunsChange?.({
                        ...(filteredNormalizedRuns as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredNormalizedRunsChange?.({
                        ...(filteredNormalizedRuns as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={NORMALIZED_RUNS_PRESETS}
                    onClear={() => {
                      onFilteredNormalizedRunsChange?.(undefined);
                      toggleNormalizedRunsFilter(false);
                    }}
                  />
                );
              case 'errorRate':
                return (
                  <ThresholdFilterComponent
                    key={filter.type}
                    label="Failing"
                    value={filteredErrorRate?.value ?? 0}
                    operator={filteredErrorRate?.operator ?? 'gte'}
                    onOperatorChange={(operator) => {
                      onFilteredErrorRateChange?.({
                        ...(filteredErrorRate as ThresholdFilter),
                        operator,
                      });
                    }}
                    onValueChange={(value) => {
                      onFilteredErrorRateChange?.({
                        ...(filteredErrorRate as ThresholdFilter),
                        value,
                      });
                    }}
                    presetValues={ERROR_RATE_PRESETS}
                    unit="%"
                    onClear={() => {
                      onFilteredErrorRateChange?.(undefined);
                      toggleErrorRateFilter(false);
                    }}
                  />
                );
              default:
                return null;
            }
          })}
          <Menu.Menu>
            <Menu.Target>
              <div>
                <FiltersMenuButton>
                  <FiltersIcon
                    icon={hasFilters ? 'menu.plus' : 'other.filters'}
                    size={hasFilters ? '14px' : '20px'}
                  />
                  {!hasFilters ? 'Filters' : ''}
                </FiltersMenuButton>
              </div>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item onClick={() => toggleServiceFilter(true)}>Services</Menu.Item>
              <Menu.Divider />
              <Menu.Item onClick={() => toggleNormalizedRunsFilter(true)}>
                Invoked often
              </Menu.Item>
              <Menu.Item onClick={() => toggleDurationAvgFilter(true)}>
                Exceeding duration
              </Menu.Item>
              <Menu.Item onClick={() => toggleErrorRateFilter(true)}>Failing</Menu.Item>
            </Menu.Dropdown>
          </Menu.Menu>
          {hasFilters ? (
            <ClearAllButton
              onClick={() => {
                toggleServiceFilter(false);
                toggleDurationAvgFilter(false);
                toggleNormalizedRunsFilter(false);
                toggleErrorRateFilter(false);

                onClearAllFilters();
              }}
            >
              Clear All
            </ClearAllButton>
          ) : null}
        </TableContainer.Filters>
      </TableContainer.Header>
      <HorizontalContainer>
        <DefaultTable
          table={table}
          label="Endpoints Table"
          loading={loading}
          loadingRows={DEFAULT_PAGE_SIZE}
          options={{
            emptyStateProps: {
              icon: 'webviewMenu.endpoint',
              expanded: fullPageTable,
              title: <EndpointsTableEmptyState />,
            },
          }}
        />
      </HorizontalContainer>
      <TableContainer.Footer>
        <Pagination
          pages={pages}
          currentPage={currentPage}
          onPageChange={onPageChange}
          loading={loading}
        />
      </TableContainer.Footer>
    </TableContainer.Container>
  );
}
